import React, { useState, useEffect } from "react";
import {
    Card, CardBody, Col, Button, Spinner, Container,
    Modal, Table, Nav, NavItem, NavLink
} from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn, H3 } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import { Edit, Plus, Trash2, Eye, Clock } from 'react-feather';
import { useAuth } from "../../Context/AuthContext";

interface TimingData {
    id: number;
    service_start_time: string;
    service_end_time: string;
    slot_duration_mins: number;
    max_service_hours: number;
    bystander_response_timeout_mins: number;
    bystander_planned_response_timeout_hrs: number;
    modify_before_hrs: number;
    radius: number;
}

export default function ViewTimingSettings() {
    const [data, setData] = useState<TimingData[]>([]);
    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(0);
    const [viewModal, setViewModal] = useState(false);
    const [viewData, setViewData] = useState<TimingData | null>(null);
    const [viewLoading, setViewLoading] = useState(false);
    const navigate = useNavigate();
    const { hasPermission } = useAuth();

    useEffect(() => {
        fetchData();
    }, [refresh]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response: any = await apiPost("timing/list", {});
            if (response && response.data) {
                setData(response.data);
            } else {
                setData([]);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to load timing settings");
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => navigate(`${process.env.PUBLIC_URL}/add_timing_settings`);
    const handleEdit = (id: number) => navigate(`${process.env.PUBLIC_URL}/edit_timing_settings`, { state: { id } });

    const handleView = async (id: number) => {
        setViewModal(true);
        setViewLoading(true);
        try {
            const data: any = await apiPost('timing/viewbyid', { id });
            setViewData(data);
        } catch (err) {
            toast.error('Failed to load timing details');
            setViewModal(false);
        } finally {
            setViewLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost(`timing/delete`, { id });
                    toast.success("Timing Settings deleted successfully", { autoClose: 1000 });
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Failed to delete timing settings", { autoClose: 1000 });
                }
            }
        });
    };

    const columns = [
        {
            name: <div><span className="fw-bold">Sl. No.</span></div>,
            selector: (row: TimingData, rowIndex?: number) => (rowIndex || 0) + 1,
            sortable: true,
        },
        {
            name: <div><span className="fw-bold">Start Time</span></div>,
            selector: (row: TimingData) => row.service_start_time,
            sortable: true,
        },
        {
            name: <div><span className="fw-bold">End Time</span></div>,
            selector: (row: TimingData) => row.service_end_time,
            sortable: true,
        },
        {
            name: <div><span className="fw-bold">Slot (Mins)</span></div>,
            selector: (row: TimingData) => `${row.slot_duration_mins} Mins`,
        },
        {
            name: <div><span className="fw-bold">Max Hours</span></div>,
            selector: (row: TimingData) => `${row.max_service_hours} Hours`,
        },
        {
            name: <div><span className="fw-bold">Response Timeout</span></div>,
            selector: (row: TimingData) => `${row.bystander_response_timeout_mins} Mins`,
        },
        {
            name: <div><span className="fw-bold">Planned Timeout</span></div>,
            selector: (row: TimingData) => `${row.bystander_planned_response_timeout_hrs} Hrs`,
        },
        {
            name: <div><span className="fw-bold">Modify Before</span></div>,
            selector: (row: TimingData) => `${row.modify_before_hrs} Hrs`,
        },
        {
            name: <div><span className="fw-bold">Search Radius</span></div>,
            selector: (row: TimingData) => `${row.radius} KM`,
        },
        {
            name: <div><span className="fw-bold">Actions</span></div>,
            cell: (row: TimingData) => (
                <div className="d-flex justify-content-between gap-2">
                    <Button size="sm" color="primary" onClick={() => handleView(row.id)} title="View">
                        <Eye size={14} />
                    </Button>
                    {hasPermission('timing-settings.edit') && (
                        <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
                            <Edit size={14} />
                        </Button>
                    )}
                    {hasPermission('timing-settings.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete">
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: { fontSize: '15px', fontWeight: '600', paddingLeft: '16px', paddingRight: '16px' },
        },
        rows: {
            style: { minHeight: '50px' },
        },
    };

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="Timing Settings" parent="Master Settings" title="Timing Settings" />
            <Container fluid>
                <Col xl={12}>
                    <Card>
                        <CardBody>
                            <Col xs={12} className="d-flex justify-content-between align-items-center mb-4">
                                <H3 className="text-primary">Service Timing Configuration</H3>
                                {data.length === 0 && !loading && hasPermission('timing-settings.add') && (
                                    <Btn color="primary" onClick={handleAdd}>
                                        <Plus size={16} className="me-2" /> Add Timing Settings
                                    </Btn>
                                )}
                            </Col>

                            <div className="table-responsive">
                                <DataTable
                                    data={data}
                                    columns={columns}
                                    progressPending={loading}
                                    progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                    highlightOnHover
                                    customStyles={customStyles}
                                    noDataComponent={
                                        <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "20vh" }}>
                                            <Card className="shadow-sm border-0 text-center" style={{ maxWidth: "320px", width: "100%" }}>
                                                <CardBody>
                                                    <h6 className="fw-semibold mb-1">No Timing Settings Found</h6>
                                                    <small className="text-muted">You haven't created any timing settings yet.</small>
                                                </CardBody>
                                            </Card>
                                        </div>
                                    }
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Container>

            {/* View Modal */}
            <Modal isOpen={viewModal} toggle={() => setViewModal(!viewModal)} centered size="md" className="premium-modal">
                {viewData && (
                    <>
                        <div style={{
                            background: 'linear-gradient(135deg, #1e6ec8 0%, #0d4fa0 100%)',
                            padding: '1.5rem',
                            position: 'relative',
                            width: '100%'
                        }}>
                            <button
                                className="btn-close btn-close-white"
                                type="button"
                                onClick={() => setViewModal(false)}
                                style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', opacity: 0.9, }}
                            />
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                                <div style={{
                                    width: '60px', height: '60px', borderRadius: '50%',
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0
                                }}>
                                    <Clock size={28} color="white" />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <h3 style={{ color: 'white', fontWeight: 700, fontSize: '1.4rem', margin: 0, marginBottom: '0.25rem' }}>
                                        Timing Configuration
                                    </h3>
                                    <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                                        <span style={{ fontWeight: 600 }}>Master Settings</span>
                                        <span className="mx-2">•</span>
                                        <span>System Settings</span>
                                    </div>
                                </div>
                                <span style={{
                                    backgroundColor: 'rgba(34,197,94,0.25)',
                                    color: 'white',
                                    padding: '0.4rem 1rem',
                                    borderRadius: '20px',
                                    fontSize: '0.8rem',
                                    fontWeight: 700,
                                    border: '1px solid rgba(34,197,94,0.5)',
                                    whiteSpace: 'nowrap'
                                }}>
                                    Active
                                </span>
                            </div>
                        </div>

                        {/* Tab-style bar to match General Settings */}
                        <div className="premium-modal-tabs border-bottom bg-white px-4">
                            <Nav tabs style={{ border: 'none', marginBottom: '-1px' }}>
                                <NavItem>
                                    <NavLink className="active" style={{ border: 'none', borderBottom: '2px solid #2563eb', color: '#2563eb', fontWeight: 600, padding: '1rem 0', cursor: 'default' }}>
                                        <Clock size={14} className="me-2" /> Timing Info
                                    </NavLink>
                                </NavItem>
                            </Nav>
                        </div>
                    </>
                )}
                
                <div className="modal-body p-0">
                    {viewLoading ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                            <Spinner color="primary" />
                        </div>
                    ) : viewData ? (
                        <div className="p-4" style={{ backgroundColor: '#f8f9fa' }}>
                            <div className="bg-white rounded shadow-sm overflow-hidden border">
                                <Table borderless className="mb-0">
                                    <tbody>
                                        <tr className="border-bottom">
                                            <td className="ps-4 py-3 text-muted fw-bold" style={{ width: '50%', fontSize: '0.85rem' }}>SERVICE START TIME</td>
                                            <td className="pe-4 py-3 text-end fw-bold text-primary">{viewData.service_start_time}</td>
                                        </tr>
                                        <tr className="border-bottom">
                                            <td className="ps-4 py-3 text-muted fw-bold" style={{ fontSize: '0.85rem' }}>SERVICE END TIME</td>
                                            <td className="pe-4 py-3 text-end fw-bold text-primary">{viewData.service_end_time}</td>
                                        </tr>
                                        <tr className="border-bottom">
                                            <td className="ps-4 py-3 text-muted fw-bold" style={{ fontSize: '0.85rem' }}>SLOT DURATION</td>
                                            <td className="pe-4 py-3 text-end fw-bold">{viewData.slot_duration_mins} Minutes</td>
                                        </tr>
                                        <tr className="border-bottom">
                                            <td className="ps-4 py-3 text-muted fw-bold" style={{ fontSize: '0.85rem' }}>MAX SERVICE HOURS</td>
                                            <td className="pe-4 py-3 text-end fw-bold">{viewData.max_service_hours} Hours</td>
                                        </tr>
                                        <tr className="border-bottom">
                                            <td className="ps-4 py-3 text-muted fw-bold" style={{ fontSize: '0.85rem' }}>BYSTANDER TIMEOUT</td>
                                            <td className="pe-4 py-3 text-end fw-bold text-danger">{viewData.bystander_response_timeout_mins} Minutes</td>
                                        </tr>
                                        <tr className="border-bottom">
                                            <td className="ps-4 py-3 text-muted fw-bold" style={{ fontSize: '0.85rem' }}>PLANNED TIMEOUT</td>
                                            <td className="pe-4 py-3 text-end fw-bold text-danger">{viewData.bystander_planned_response_timeout_hrs} Hours</td>
                                        </tr>
                                        <tr className="border-bottom">
                                            <td className="ps-4 py-3 text-muted fw-bold" style={{ fontSize: '0.85rem' }}>MODIFY BEFORE</td>
                                            <td className="pe-4 py-3 text-end fw-bold text-danger">{viewData.modify_before_hrs} Hours</td>
                                        </tr>
                                        <tr>
                                            <td className="ps-4 py-3 text-muted fw-bold" style={{ fontSize: '0.85rem' }}>SEARCH RADIUS</td>
                                            <td className="pe-4 py-3 text-end fw-bold">{viewData.radius} KM</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    ) : null}
                </div>
            </Modal>

            <ToastContainer />
        </div>
    );
}
